import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Stack,
  Text,
  Paper,
  Group,
  Badge,
  Box,
  ScrollArea,
  Avatar,
} from "@mantine/core";
import { IconClock, IconUser } from "@tabler/icons-react";
import moment from "moment";
import { GetTransactionsResponse } from "../../../../../types/transactions";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
import { AppointmentStatus } from "../../../../../types/calendar";
import { EventFlags } from "./EventBlock";
import { transactionFlags } from "../../flags/calendarFlags";
import { badgeSettingsFrom } from "../../../../../utils/badgeSettings";
import blockStyles from "./EventBlock.module.css";
import CalendarToolbar from "./CalendarToolbar";
import styles from "./ListView.module.css";

interface ListViewProps {
  transactions: GetTransactionsResponse[];
  onSelectTransaction: (transaction: GetTransactionsResponse) => void;
}

interface GroupedTransactions {
  [key: string]: {
    label: string;
    date: string;
    transactions: GetTransactionsResponse[];
  };
}

const STATUS_COLORS: Record<string, string> = {
  Requested: "yellow",
  Accepted: "orange",
  Confirmed: "teal",
  Completed: "green",
  "No Show": "red",
  Declined: "gray",
  Cancelled: "gray",
};

const ListView: React.FC<ListViewProps> = ({
  transactions,
  onSelectTransaction,
}) => {
  const {
    selectedDate,
    dateRange,
    selectedServiceTypes,
    selectedArtists,
    selectedStatuses,
    filteredEmployees,
  } = useCalendar();
  const { businessInfo, services: salonServices } = useSalon() as {
    businessInfo: { timezone: string } | null;
    services: any[];
  };

  // Refs for scroll detection and today section
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  // State for virtual scrolling window
  const [visibleDateKeys, setVisibleDateKeys] = useState<string[]>([]);
  const [hasMoreNext, setHasMoreNext] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingNext, setIsLoadingNext] = useState(false);

  // Create set of active staff IDs for efficient lookup
  const activeStaffIds = useMemo(() => {
    return new Set(filteredEmployees.map((emp) => emp.id));
  }, [filteredEmployees]);

  // Filter and group transactions by date
  const groupedTransactions = useMemo(() => {
    const filtered = transactions.filter((t) => {
      // Filter out appointments from inactive staff members
      const hasActiveStaff = t.line_items.booked_services.some((s) =>
        activeStaffIds.has(s.staff_id),
      );
      if (!hasActiveStaff) return false;

      // Filter by service type
      if (!selectedServiceTypes.includes("all")) {
        const hasMatchingService = t.line_items.booked_services.some((s) =>
          selectedServiceTypes.includes(s.service_type),
        );
        if (!hasMatchingService) return false;
      }

      // Filter by staff
      if (!selectedArtists.includes("all")) {
        const hasMatchingStaff = t.line_items.booked_services.some((s) =>
          selectedArtists.includes(s.staff_id),
        );
        if (!hasMatchingStaff) return false;
      }

      // Filter by appointment status (default hides Cancelled/Declined;
      // toggleable via the Filters dropdown Status column)
      const status = t.transaction.appointment_status;
      if (status && !selectedStatuses.includes(status as AppointmentStatus))
        return false;

      return true;
    });

    // Sort by date
    const sorted = [...filtered].sort((a, b) => {
      const aDate = a.line_items.booked_services[0]?.start_dt;
      const bDate = b.line_items.booked_services[0]?.start_dt;
      return moment(aDate).diff(moment(bDate));
    });

    // Group by date with labels
    const grouped: GroupedTransactions = {};
    const today = moment().startOf("day");
    const tomorrow = moment().add(1, "day").startOf("day");

    sorted.forEach((transaction) => {
      const firstService = transaction.line_items.booked_services[0];
      if (!firstService) return;

      const transactionDate = moment
        .utc(firstService.start_dt)
        .tz(businessInfo?.timezone || "America/New_York")
        .startOf("day");
      const dateKey = transactionDate.format("YYYY-MM-DD");

      let label = transactionDate.format("dddd, MMMM D");

      if (transactionDate.isSame(today, "day")) {
        label = "Today";
      } else if (transactionDate.isSame(tomorrow, "day")) {
        label = "Tomorrow";
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          label,
          date: dateKey,
          transactions: [],
        };
      }

      grouped[dateKey].transactions.push(transaction);
    });

    return grouped;
  }, [
    transactions,
    selectedServiceTypes,
    selectedArtists,
    selectedStatuses,
    activeStaffIds,
    businessInfo?.timezone,
  ]);

  // Get all date keys sorted chronologically
  const allDateKeys = useMemo(() => {
    return Object.keys(groupedTransactions).sort((a, b) =>
      moment(a).diff(moment(b)),
    );
  }, [groupedTransactions]);

  // Calculate initial window: Today + next 20 appointments
  const calculateInitialWindow = useCallback((): string[] => {
    const today = moment().format("YYYY-MM-DD");

    // Find dates from today onwards
    const todayAndFutureDates = allDateKeys.filter((key) =>
      moment(key).isSameOrAfter(today, "day"),
    );

    // Start with today (even if not in data)
    const result: string[] = [];
    let appointmentCount = 0;
    let dateIndex = 0;

    // If today exists in data, count its appointments
    if (todayAndFutureDates.length > 0 && todayAndFutureDates[0] === today) {
      result.push(today);
      appointmentCount += groupedTransactions[today]?.transactions.length || 0;
      dateIndex = 1;
    } else {
      // Today has no appointments, but include it anyway
      result.push(today);
    }

    // Add more dates until we have 20 appointments
    while (appointmentCount < 20 && dateIndex < todayAndFutureDates.length) {
      const dateKey = todayAndFutureDates[dateIndex];
      result.push(dateKey);
      appointmentCount +=
        groupedTransactions[dateKey]?.transactions.length || 0;
      dateIndex++;
    }

    return result;
  }, [allDateKeys, groupedTransactions]);

  // Load more next appointments (up to 20)
  const loadNext = useCallback(() => {
    if (!hasMoreNext || visibleDateKeys.length === 0 || isLoadingNext) return;

    setIsLoadingNext(true);

    const today = moment().format("YYYY-MM-DD");
    const lastVisibleKey = visibleDateKeys[visibleDateKeys.length - 1];

    // Find dates after the last visible date
    const datesAfterVisible = allDateKeys.filter((key) =>
      moment(key).isAfter(lastVisibleKey),
    );

    if (datesAfterVisible.length === 0) {
      setHasMoreNext(false);
      setIsLoadingNext(false);
      return;
    }

    const datesToAdd: string[] = [];
    let appointmentCount = 0;
    let currentIndex = 0;

    // Collect dates until we have 20 appointments
    while (appointmentCount < 20 && currentIndex < datesAfterVisible.length) {
      const dateKey = datesAfterVisible[currentIndex];
      datesToAdd.push(dateKey);
      appointmentCount +=
        groupedTransactions[dateKey]?.transactions.length || 0;
      currentIndex++;
    }

    if (datesToAdd.length > 0) {
      setVisibleDateKeys((prev) => [...prev, ...datesToAdd]);
    }

    // Check if there are more dates after what we just added
    setHasMoreNext(currentIndex < datesAfterVisible.length);
    setIsLoadingNext(false);
  }, [
    visibleDateKeys,
    allDateKeys,
    groupedTransactions,
    hasMoreNext,
    isLoadingNext,
  ]);

  // Initialize visible window on mount or when data changes
  useEffect(() => {
    setIsLoadingNext(false);

    if (allDateKeys.length === 0) {
      // No appointments at all - show today with message
      const today = moment().format("YYYY-MM-DD");
      setVisibleDateKeys([today]);
      setHasMoreNext(false);
      setIsInitialized(true);
      return;
    }

    const initialWindow = calculateInitialWindow();
    setVisibleDateKeys(initialWindow);

    // Check if there are more dates after the initial window
    const lastKey = initialWindow[initialWindow.length - 1];
    const datesAfterLast = allDateKeys.filter((key) =>
      moment(key).isAfter(lastKey),
    );
    setHasMoreNext(datesAfterLast.length > 0);
    setIsInitialized(true);
  }, [allDateKeys, calculateInitialWindow]);

  // Set up IntersectionObserver for infinite scroll (bottom only)
  useEffect(() => {
    if (!isInitialized || !hasMoreNext) return;

    const bottomObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingNext) {
          loadNext();
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    if (bottomSentinelRef.current) {
      bottomObserver.observe(bottomSentinelRef.current);
    }

    return () => {
      bottomObserver.disconnect();
    };
  }, [isInitialized, hasMoreNext, isLoadingNext, loadNext]);

  const today = moment().format("YYYY-MM-DD");

  return (
    <Box className={styles.root}>
      {/* Filters pill only: the list is today-anchored, so no date controls. */}
      <CalendarToolbar view="list" filtersOnly />
      <ScrollArea className={styles.container}>
        <Stack gap="lg" p="md">
          {/* Render visible date keys */}
          {visibleDateKeys.map((dateKey) => {
            const group = groupedTransactions[dateKey];
            const isToday = dateKey === today;

            // Handle today with no appointments
            if (isToday && !group) {
              return (
                <div key={dateKey} className={styles.dateGroup}>
                  <Group gap="sm" mb="sm" className={styles.dateHeader}>
                    <Box
                      className={`${styles.dateDot} ${styles.dateDotToday}`}
                    />
                    <Text fw={500} size="sm">
                      Today
                    </Text>
                  </Group>
                  <Box className={styles.emptyTodayMessage}>
                    <Text size="sm" c="dimmed" ta="center">
                      No appointments today
                    </Text>
                  </Box>
                </div>
              );
            }

            // Skip if no group (shouldn't happen except for today)
            if (!group) return null;

            return (
              <div key={dateKey} className={styles.dateGroup}>
                {/* Date Header */}
                <Group gap="sm" mb="sm" className={styles.dateHeader}>
                  <Box
                    className={`${styles.dateDot} ${isToday ? styles.dateDotToday : ""}`}
                  />
                  <Text fw={500} size="sm">
                    {group.label}
                  </Text>
                </Group>

                {/* Transactions for this date */}
                <Stack gap="sm" pl="lg">
                  {group.transactions.map((transaction) => (
                    <TransactionCard
                      key={transaction.transaction.id}
                      transaction={transaction}
                      onClick={() => onSelectTransaction(transaction)}
                      timezone={businessInfo?.timezone || "America/New_York"}
                      salonServices={salonServices}
                    />
                  ))}
                </Stack>
              </div>
            );
          })}

          {/* Bottom sentinel for loading next appointments */}
          {hasMoreNext && (
            <div
              ref={bottomSentinelRef}
              style={{ height: "20px", padding: "10px 0" }}
            >
              {isLoadingNext && (
                <Text size="xs" c="dimmed" ta="center">
                  Loading more...
                </Text>
              )}
            </div>
          )}
        </Stack>
      </ScrollArea>
    </Box>
  );
};

// Transaction Card Component
interface TransactionCardProps {
  transaction: GetTransactionsResponse;
  onClick: () => void;
  timezone: string;
  salonServices?: any[];
}

const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  onClick,
  timezone,
  salonServices,
}) => {
  const { businessInfo, employees } = useSalon() as {
    businessInfo?: unknown;
    employees?: {
      id: string;
      staff_photo_url?: string | null;
      avatar_url?: string | null;
    }[];
  };
  const badgeSettings = badgeSettingsFrom(businessInfo);
  const firstService = transaction.line_items.booked_services[0];
  if (!firstService) return null;

  const staff = employees?.find((e) => e.id === firstService.staff_id);
  const staffPhoto = staff?.staff_photo_url ?? staff?.avatar_url ?? undefined;
  const clientName = `${transaction.client.first_name} ${transaction.client.last_name}`;
  const serviceName = firstService.name;
  const staffName = firstService.staff_name;
  const staffInitials = staffName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2);

  const startTime = moment
    .utc(firstService.start_dt)
    .tz(timezone)
    .format("h:mm A");
  const endTime = moment
    .utc(firstService.calc__end_dt)
    .tz(timezone)
    .format("h:mm A");

  const salonService = salonServices?.find(
    (s: any) => s.id === firstService.salon_service_id,
  );
  const serviceColor =
    salonService?.default_calendar_event_color ||
    firstService.calendar_event_color ||
    "#36AFC9";
  const flags = transactionFlags(transaction, timezone, badgeSettings);

  return (
    <Paper
      className={`${styles.transactionCard} ${blockStyles.tintCard}`}
      radius={8}
      onClick={onClick}
      style={{ "--svc": serviceColor } as React.CSSProperties}
    >
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          {/* Time Range */}
          <Group gap={4}>
            <IconClock size={12} color="var(--text-muted)" />
            <Text size="xs" c="dimmed" className={blockStyles.monoTime}>
              {`${startTime} – ${endTime}`}
            </Text>
          </Group>

          {/* Service Name */}
          <Text size="sm" c="dimmed">
            {serviceName}
          </Text>

          {/* Client Name */}
          <Text size="sm" fw={500} lineClamp={1}>
            {clientName}
          </Text>
        </Stack>

        {/* Flag chips (top-right) + Staff Avatar */}
        <Stack gap={4} align="flex-end">
          <EventFlags flags={flags} inline />
          <Avatar size="sm" radius="xl" color="teal" src={staffPhoto}>
            {staffInitials}
          </Avatar>
        </Stack>
      </Group>
    </Paper>
  );
};

export default ListView;
